import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Grid,
  Pagination,
  Typography,
  Card,
  CardContent,
  Chip,
  Rating,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
} from "@mui/material";
import DataTable from "../../../../../components/ui/DataTable";
import SearchTableData from "../../../../../utils/SearchTableData";
import Loader from "../../../../../components/ui/LoaderContainer";
import RefreshPage from "../../../../../utils/RefreshPage";
import DateRangeFilter from "../../../../../components/ui/DatePicker";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";

export default function BadReviews({ viewAllBadReview, login, client }) {
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const fetchData = useCallback(() => {
    viewAllBadReview(
      login.token,
      login.user_id,
      searchInput,
      page,
      rowsPerPage,
      startDate,
      endDate,
      ""
    );
  }, [
    viewAllBadReview,
    login.token,
    login.user_id,
    searchInput,
    page,
    rowsPerPage,
    startDate,
    endDate,
  ]);

  useEffect(() => {
    document.body.style.background = "#f0f2f5"; // Softer background
    fetchData();
  }, [page, startDate, endDate, fetchData]);

  useEffect(() => {
    const timer = setTimeout(fetchData, 500);
    return () => clearTimeout(timer);
  }, [searchInput, fetchData]);

  const handleDateChange = ({ start, end }) => {
    setStartDate(start);
    setEndDate(end);
    setPage(1);
  };

  const processedData = useMemo(() => {
    return (
      client?.badfeedback?.map((item) => {
        const createdAt = new Date(item.createdAt);
        const date = createdAt.toLocaleDateString();
        const time = createdAt.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });

        return {
          ...item,
          ratingDisplay: (
            <Rating value={item.rating || 0} readOnly size="small" precision={0.5} />
          ),
          mobileDisplay: item.mobile ? (
            <a href={`tel:${item.mobile}`} style={{ textDecoration: "none", color: "inherit", fontWeight: 500 }}>
              {item.mobile}
            </a>
          ) : "-",
          emailDisplay: item.email ? (
            <a href={`mailto:${item.email}`} style={{ textDecoration: "none", color: "inherit", fontWeight: 500 }}>
              {item.email}
            </a>
          ) : "-",
          datetime: (
            <Box display="flex" alignItems="center" gap={0.5}>
              <AccessTimeIcon sx={{ fontSize: 14, color: "text.secondary" }} />
              <Typography variant="caption" color="text.secondary">
                {date} {time}
              </Typography>
            </Box>
          ),
        };
      }) || []
    );
  }, [client?.badfeedback]);

  const columns = [
    { header: "Name", key: "name" },
    { header: "Rating", key: "ratingDisplay" },
    { header: "Mobile", key: "mobileDisplay" },
    { header: "Email", key: "emailDisplay" },
    { header: "Query", key: "desc" },
    { header: "Date Time", key: "datetime" },
  ];

  // Mobile Card Component
  const ReviewCard = ({ item }) => (
    <Card
      sx={{
        mb: 2,
        borderRadius: 3,
        boxShadow: "0 2px 12px 0 rgba(0,0,0,0.05)",
        border: "1px solid rgba(0,0,0,0.05)",
        overflow: "visible",
      }}
    >
      <CardContent>
        <Stack spacing={1.5}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                {item.name || "Anonymous"}
              </Typography>
              <Rating value={item.rating || 0} readOnly size="small" />
            </Box>
            {item.datetime}
          </Box>

          <Divider sx={{ borderStyle: "dashed" }} />

          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", bgcolor: "#f9fafb", p: 1.5, borderRadius: 2 }}>
            "{item.desc}"
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {item.mobile && (
              <Chip
                component="a"
                href={`tel:${item.mobile}`}
                clickable
                icon={<PhoneIcon sx={{ fontSize: 16 }} />}
                label={item.mobile}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer", color: "text.primary" }}
              />
            )}
            {item.email && (
              <Chip
                component="a"
                href={`mailto:${item.email}`}
                clickable
                icon={<EmailIcon sx={{ fontSize: 16 }} />}
                label={item.email}
                size="small"
                variant="outlined"
                sx={{ fontSize: "0.75rem", textDecoration: "none", cursor: "pointer", color: "text.primary" }}
              />
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box"
      }}
    >
      <Grid container spacing={3}>
        {/* Header Section */}
        <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              background: "linear-gradient(45deg, #031634, #2c3e50)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent"
            }}
          >
            Bad Reviews
          </Typography>
          <RefreshPage />
        </Grid>

        {/* Filters Section */}
        <Grid item xs={12}>
          <Box
            sx={{
              p: 2,
              borderRadius: 3,
              bgcolor: "#fff",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)",
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: "center"
            }}
          >
            <Box sx={{ width: { xs: "100%", md: "40%" } }}>
              <SearchTableData onSearch={(value) => setSearchInput(value)} />
            </Box>
            <Box sx={{ width: { xs: "100%", md: "60%" }, display: 'flex', justifyContent: { xs: 'center', md: 'flex-end' } }}>
              <DateRangeFilter onDateChange={handleDateChange} />
            </Box>
          </Box>
        </Grid>

        {/* Content Section */}
        <Grid item xs={12}>
          {isMobile ? (
            // Mobile View: Cards
            <Box>
              {processedData.length > 0 ? (
                processedData.map((item, index) => (
                  <ReviewCard key={item._id || index} item={item} />
                ))
              ) : (
                <Typography textAlign="center" color="text.secondary" py={4}>
                  No bad reviews found.
                </Typography>
              )}
            </Box>
          ) : (
            // Desktop View: Table
            <Box
              sx={{
                bgcolor: "#fff",
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                overflow: "hidden"
              }}
            >
              {processedData.length > 0 ? (
                <DataTable data={processedData} columns={columns} />
              ) : (
                <Typography textAlign="center" color="text.secondary" py={4}>
                  No bad reviews found.
                </Typography>
              )}
            </Box>
          )}

          {/* Pagination */}
          {client?.badfeedback_length > 0 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={Math.max(1, Math.ceil((client?.badfeedback_length || 0) / rowsPerPage))}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Grid>
      </Grid>
      <Loader />
    </Box>
  );
}
